import { mmFetchWithResponse } from 'api/mmFetchWithResponse';

/**
 * Merch line-item search: `GET /api/inventory/v1/fc-transfers/items`
 * (distinct from transfer-level `GET /api/inventory/v1/fc-transfers`).
 */
const FC_TRANSFER_ITEMS_SEARCH_PATH = 'api/inventory/v1/fc-transfers/items';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
function parseIntFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) && value > 0 ? value : undefined;
    }
    const trimmed = String(value).trim();
    if (!trimmed) return undefined;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n <= 0 || String(n) !== trimmed) {
        return undefined;
    }
    return n;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function firstNonEmptyString(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            if (item !== undefined && item !== null && String(item).trim()) {
                return String(item).trim();
            }
        }
    }
    return '';
}

/**
 * Builds URLSearchParams for `GET /inventory/v1/fc-transfers/items`.
 * Sort is fixed server-side (transferItemId desc) — no sort params.
 *
 * @param {Object} [params={}]
 * @returns {URLSearchParams}
 */
export function buildFCTransferItemsSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.pageSize !== undefined && params.pageSize !== null && params.pageSize !== '') {
        queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.cursor !== undefined && params.cursor !== null && params.cursor !== '') {
        queryParams.set('cursor', String(params.cursor));
    }

    const transferId = parseIntFilter(params.transferId);
    if (transferId !== undefined) {
        queryParams.set('transferId', String(transferId));
    }

    const sourceFacilityId = parseIntFilter(params.sourceFacilityId);
    if (sourceFacilityId !== undefined) {
        queryParams.set('sourceFacilityId', String(sourceFacilityId));
    }

    const destinationFacilityId = parseIntFilter(params.destinationFacilityId);
    if (destinationFacilityId !== undefined) {
        queryParams.set('destinationFacilityId', String(destinationFacilityId));
    }

    const companySourceId = parseIntFilter(params.companySourceId);
    if (companySourceId !== undefined) {
        queryParams.set('companySourceId', String(companySourceId));
    }

    const externalSku = parseIntFilter(params.externalSku);
    if (externalSku !== undefined) {
        queryParams.set('externalSku', String(externalSku));
    }

    const sourceLotId = parseIntFilter(params.sourceLotId);
    if (sourceLotId !== undefined) {
        queryParams.set('sourceLotId', String(sourceLotId));
    }

    // Single status string (e.g. "PARTIALLY COMPLETED") — URLSearchParams encodes the space.
    const transferStatus = firstNonEmptyString(params.transferStatus ?? params.transferStatuses);
    if (transferStatus) {
        queryParams.set('transferStatus', transferStatus);
    }

    const transferItemStatus = firstNonEmptyString(params.transferItemStatus);
    if (transferItemStatus) {
        queryParams.set('transferItemStatus', transferItemStatus);
    }

    if (params.hasDiscrepancy === true || params.hasDiscrepancy === 'true' || params.hasDiscrepancy === '1') {
        queryParams.set('hasDiscrepancy', 'true');
    } else if (params.hasDiscrepancy === false || params.hasDiscrepancy === 'false' || params.hasDiscrepancy === '0') {
        queryParams.set('hasDiscrepancy', 'false');
    }

    return queryParams;
}

/**
 * @param {Object} [params={}]
 * @returns {string}
 */
export function buildFCTransferItemsSearchQueryString(params = {}) {
    return buildFCTransferItemsSearchParams(params).toString();
}

/**
 * Search FC transfer line items (`GET /inventory/v1/fc-transfers/items`).
 *
 * @param {Object} [params={}]
 * @param {Object} [options={}]
 * @returns {Promise<{status: boolean, msg?: string, data?: {rows: Array, pagination: Object}}>}
 */
export async function searchFCTransferItems(params = {}, options = {}) {
    const qs = buildFCTransferItemsSearchQueryString(params);
    const url = qs.length > 0 ? `${FC_TRANSFER_ITEMS_SEARCH_PATH}?${qs}` : FC_TRANSFER_ITEMS_SEARCH_PATH;
    return mmFetchWithResponse(url, options);
}
